<template>
  <div v-if="showActionBar" class="br-admin-item-actions">
    <div v-if="!isRejectSelected" class="br-admin__actions">
      <button
        type="button"
        class="br-admin__reject-toggle"
        :aria-pressed="false"
        :disabled="processing"
        @click="onRejectToggle"
      >
        <span class="br-admin__radio" aria-hidden="true" />
        <span class="br-admin__reject-label">{{ $t('lbl_EMPBE_BR_Admin_Reject') }}</span>
      </button>
      <!-- 人工审批中(PA)默认显示；改草稿后显示；审批中(P)整栏不渲染 -->
      <button
        v-if="showProceedButton"
        type="button"
        class="br-admin__proceed-btn"
        :disabled="processing"
        @click="onProceedClick"
      >
        {{ $t('lbl_EMPBE_BR_Admin_Proceed') }}
      </button>
    </div>

    <div v-else class="br-admin__rejected-row">
      <div class="br-admin__reason-panel br-admin__reason-panel--rejected">
        <div class="br-admin__reason-panel-head">
          <button
            type="button"
            class="br-admin__rejected-title-btn"
            :aria-pressed="true"
            @click="onRejectToggle"
          >
            <span class="br-admin__rejected-icon" aria-hidden="true" />
            <span class="br-admin__reject-box-title">
              {{ $t('lbl_EMPBE_BR_Admin_RejectedReasonBelow') }}
            </span>
          </button>
        </div>
        <div class="br-admin__reason-field">
          <p
            class="br-admin__reason-preview"
            :class="{ 'br-admin__reason-preview--placeholder': !reasonPreviewText }"
          >
            {{ reasonPreviewText || $t('lbl_EMPBE_BR_Admin_RejectReasonRequired') }}
          </p>
          <button
            type="button"
            class="br-admin__template-btn"
            :aria-expanded="templateOpen"
            @click.stop="templateOpen = !templateOpen"
          >
            {{ $t('lbl_EMPBE_BR_Admin_Template') }}
            <span
              class="br-admin__template-caret"
              :class="{ 'br-admin__template-caret--up': templateOpen }"
              aria-hidden="true"
            />
          </button>
        </div>
        <div v-if="templateOpen" class="br-admin__template-panel" @click.stop>
          <div class="br-admin__template-grid">
            <label
              v-for="reason in rejectReasonOptions"
              :key="'r-' + reason.code"
              class="br-admin__template-item"
              :class="{ 'br-admin__template-item--on': selectedReasons.includes(reason.code) }"
            >
              <input
                type="checkbox"
                :checked="selectedReasons.includes(reason.code)"
                @change="$emit('toggle-reason', reason.code, $event.target.checked)"
              />
              <span>{{ reason.label }}</span>
            </label>
          </div>
          <div v-if="selectedReasons.includes('OTHER')" class="br-admin__other-reason">
            <textarea
              :value="otherReasonText"
              class="br-admin__other-input"
              rows="2"
              :placeholder="$t('lbl_EMPBE_BR_Admin_Reject_OtherPlaceholder')"
              @input="$emit('other-reason-input', $event.target.value)"
            />
          </div>
        </div>
        <p v-if="reasonError" class="br-admin__error">
          {{ $t('lbl_EMPBE_BR_Admin_RejectReasonRequired') }}
        </p>
        <p v-if="otherReasonError" class="br-admin__error">
          {{ $t('lbl_EMPBE_BR_Admin_Reject_OtherRequired') }}
        </p>
      </div>
      <button
        v-if="showProceedButton"
        type="button"
        class="br-admin__proceed-btn br-admin__proceed-btn--compact"
        :disabled="processing"
        @click="onProceedClick"
      >
        {{ $t('lbl_EMPBE_BR_Admin_Proceed') }}
      </button>
    </div>
  </div>
</template>

<script>
import { BR_STATUS, getItemStatusCode } from '../../../businessDocuments/utils/brDocument';

export default {
  name: 'BrAdminItemActions',
  props: {
    item: { type: Object, required: true },
    draft: { type: Object, default: null },
    processing: { type: Boolean, default: false },
    /** 本会话已处理过且驳回相关无新变化时禁用「处理」 */
    proceedLocked: { type: Boolean, default: false },
    rejectReasonOptions: { type: Array, default: () => [] },
    showDirectRejectBar: { type: Boolean, default: false },
    canChangeToReject: { type: Boolean, default: false },
    isLastInEffectApproved: { type: Boolean, default: false },
  },
  data() {
    return { templateOpen: false };
  },
  computed: {
    rejectBlocked() {
      return this.showDirectRejectBar
        && this.isLastInEffectApproved
        && !this.canChangeToReject;
    },
    isDraftBarMode() {
      const code = getItemStatusCode(this.item);
      // 审批中(P) 无操作栏；人工审批中(PA) / 已驳回 才展示
      if (code === BR_STATUS.PENDING) {
        return false;
      }
      return this.item?.listKind === 'pending' || code === BR_STATUS.REJECTED;
    },
    showActionBar() {
      return this.isDraftBarMode || this.showDirectRejectBar;
    },
    isRejectSelected() {
      return !!this.draft?.rejectSelected;
    },
    /** 人工审批中(PA)默认显示；非待审默认隐藏，改草稿后显示 */
    showProceedButton() {
      return !this.proceedLocked;
    },
    selectedReasons() {
      return this.draft?.reasons || [];
    },
    otherReasonText() {
      return this.draft?.otherReasonText || '';
    },
    reasonError() {
      return !!this.draft?.reasonError;
    },
    otherReasonError() {
      return !!this.draft?.otherReasonError;
    },
    reasonPreviewText() {
      const labels = [];
      this.selectedReasons.forEach((code) => {
        if (code === 'OTHER') {
          const other = String(this.otherReasonText || '').trim();
          if (other) labels.push(other);
          return;
        }
        const opt = this.rejectReasonOptions.find((r) => r.code === code);
        if (opt?.label) labels.push(opt.label);
      });
      return labels.join('; ');
    },
  },
  watch: {
    isRejectSelected(val) {
      if (!val) this.templateOpen = false;
    },
    processing(val) {
      if (val) this.templateOpen = false;
    },
  },
  methods: {
    onRejectToggle() {
      if (this.rejectBlocked && !this.isRejectSelected) {
        this.$emit('reject-last-blocked');
        return;
      }
      this.$emit('toggle-reject');
      this.templateOpen = false;
    },
    onProceedClick() {
      if (this.processing || this.proceedLocked) {
        return;
      }
      this.$emit('proceed');
    },
  },
};
</script>
